import { useEffect, useState } from 'react';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { logger } from 'modules/logger';
import { getWorkspaceManagerVersion, isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import {
  addExtensionListener,
  removeExtensionListener,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from 'domains/extension/constants';
import { getPageType, PageTypes } from 'utils/pageTypes';
import { getBlankRoute } from 'utils/urls';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { AppRoutePath } from 'config/constants';

export const EXT_VERSION_REQUIRED = 5;

export function useClearCase(): void {
  const navigate = useNavigate();
  const location = useLocation();

  const [isExtensionReady, setIsExtensionReady] = useState(() => {
    return isWorkspaceManagerAvailable() && getWorkspaceManagerVersion() >= EXT_VERSION_REQUIRED;
  });
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);

  const viewerMatch = matchPath({ path: AppRoutePath.VIEWER, end: true }, location.pathname);
  const windowId = viewerMatch?.params.windowId;

  useEffect(() => {
    const checkExtensionStatus = () => {
      const isAvailable = isWorkspaceManagerAvailable();
      const version = getWorkspaceManagerVersion();
      const isReady = isAvailable && version >= EXT_VERSION_REQUIRED;

      if (isReady && !isExtensionReady) {
        logger.info('[useClearCase] Extension became available with adequate version');
      } else if (isAvailable && version < EXT_VERSION_REQUIRED) {
        logger.warn(
          `[useClearCase] Extension is available but version ${version} is less than required ${EXT_VERSION_REQUIRED}`
        );
      }

      setIsExtensionReady(isReady);
    };

    if (!isExtensionReady) {
      const handleExtensionInjected = () => {
        logger.info('[useClearCase] Extension injection detected, checking status');
        checkExtensionStatus();
      };

      addExtensionListener(EXTENSION_RESPONSE_EVENTS.EXTENSION_INJECTED, handleExtensionInjected);

      return () => {
        removeExtensionListener(
          EXTENSION_RESPONSE_EVENTS.EXTENSION_INJECTED,
          handleExtensionInjected
        );
      };
    }
  }, [isExtensionReady]);

  useEffect(() => {
    if (!isExtensionReady || !isClearPatientContextEnabled) {
      return;
    }

    const clearCaseHandler = () => {
      const currentPageType = getPageType(window.location.href);
      let blankPath = getBlankRoute(currentPageType);

      if (!blankPath) {
        return;
      }

      logger.info('[useClearCase] Starting to clear case context for current page', {
        currentPageType,
      });

      if (currentPageType === PageTypes.VIEWER) {
        if (windowId == null) {
          logger.warn(
            '[useClearCase] Could not determine viewer windowId; skipping clear-case navigation',
            {
              currentPath: location.pathname,
            }
          );
          return;
        }

        blankPath = generatePath(blankPath, { windowId });
      }

      const isSameRoute = !!matchPath(
        { path: blankPath, end: currentPageType !== PageTypes.REPORTER },
        window.location.pathname
      );

      if (!isSameRoute) {
        navigate(blankPath);
        logger.info('[useClearCase] Navigating to blank route', {
          currentPageType,
          currentPath: window.location.pathname,
          blankPath,
        });
      }
    };

    addExtensionListener(EXTENSION_RESPONSE_EVENTS.CLEAR_CASE_TRIGGERED, clearCaseHandler);

    return () => {
      removeExtensionListener(EXTENSION_RESPONSE_EVENTS.CLEAR_CASE_TRIGGERED, clearCaseHandler);
    };
  }, [isClearPatientContextEnabled, isExtensionReady, location.pathname, navigate, windowId]);
}
